import { z } from 'zod'
import { requiredEmailSchema } from './email.ts'

/**
 * Atomic regex parts for password requirements. Exported so UIs that need
 * granular feedback (e.g. a strength-meter checklist showing which specific
 * rule is unmet) can test individual criteria without re-declaring them.
 */
export const HAS_UPPERCASE = /[A-Z]/
export const HAS_SPECIAL = /[^a-zA-Z0-9]/

/**
 * Password validation pattern requiring:
 * - At least one uppercase letter
 * - At least one special (non-alphanumeric) character
 *
 * Composed from HAS_UPPERCASE / HAS_SPECIAL via `.source` so the combined
 * regex cannot drift from the per-criterion regexes used by UI checklists.
 * Minimum/maximum length are enforced separately via newPasswordSchema.
 */
export const PASSWORD_PATTERN = new RegExp(
  `^(?=.*${HAS_UPPERCASE.source})(?=.*${HAS_SPECIAL.source})`,
)

/** Error message for password pattern validation */
export const PASSWORD_PATTERN_ERROR =
  'Password must contain at least one uppercase letter and one special character'

/**
 * Reusable Zod validator for any "set new password" flow.
 * Enforces length [8, 128] AND complexity (uppercase + special character).
 * The 128 upper bound mirrors the edge-function DoS guard so the client and
 * server reject the same inputs.
 */
export const newPasswordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password must not exceed 128 characters')
  .regex(PASSWORD_PATTERN, PASSWORD_PATTERN_ERROR)

export const loginSchema = z.object({
  email: requiredEmailSchema,
  password: z
    .string()
    .min(1, 'Password is required')
    // Intentionally weaker than newPasswordSchema: login authenticates an
    // EXISTING password, which may predate the uppercase+special rule.
    // Tightening this would lock out legacy users; the authoritative check
    // is Supabase Auth verifying the credential itself.
    .min(8, 'Password must be at least 8 characters'),
})

export const registerSchema = z
  .object({
    email: requiredEmailSchema,
    password: newPasswordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    fullName: z.string().min(1, 'Full name is required'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export const forgotPasswordSchema = z.object({
  email: requiredEmailSchema,
})

export const resetPasswordSchema = z
  .object({
    password: newPasswordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

export const acceptInvitationSchema = z
  .object({
    password: newPasswordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

/**
 * User role enum matching database user_role type
 */
export const userRoleSchema = z.enum([
  'super_admin',
  'admin',
  'org_owner',
  'tech_team',
  'user',
  'viewer',
])

/**
 * Roles that can access multiple organizations
 */
export const PLATFORM_STAFF_ROLES = ['super_admin', 'admin'] as const

/**
 * Whether a role is platform staff — DCP employees who operate across tenants,
 * as opposed to members of one customer organization.
 *
 * The single source of truth for that list. Consumers must call this (or
 * `PLATFORM_STAFF_ROLES`) rather than re-listing the literals: the same check
 * runs in the web app AND in Edge Functions, and two hand-maintained copies
 * drifting apart is precisely the class of bug DCP-349 fixed (Presto PR #201).
 *
 * Note this is about WHO the caller is, not what they may do. Anything a
 * tenant's own Admin can also do belongs in {@link PRODUCT_ROLE_PERMISSIONS},
 * gated by {@link hasPermission}.
 */
export function isPlatformStaff(role: UserRole | null | undefined): boolean {
  if (!role) return false
  return PLATFORM_STAFF_ROLES.includes(role as (typeof PLATFORM_STAFF_ROLES)[number])
}

/**
 * Check if a role can switch between organizations — which is exactly the
 * platform-staff privilege, so it delegates rather than repeating the list.
 */
export function canSwitchOrganizations(role: UserRole): boolean {
  return isPlatformStaff(role)
}

/**
 * Invitation type for organization invitations
 */
export const inviteTypeSchema = z.enum(['organization', 'admin', 'user'])

/**
 * Roles that can access admin panel
 */
export const ADMIN_PANEL_ROLES = ['super_admin', 'admin'] as const

/**
 * Roles that can invite users to organizations
 */
export const CAN_INVITE_ROLES = ['super_admin', 'admin', 'org_owner'] as const

/**
 * Check if role can access admin panel (/admin/*)
 */
export function canAccessAdminPanel(role: UserRole): boolean {
  return ADMIN_PANEL_ROLES.includes(role as (typeof ADMIN_PANEL_ROLES)[number])
}

/**
 * Roles allowed to edit the organization (company) name — DCP-158.
 * "Account Owner (Client Admin)" maps to the system role 'org_owner'.
 */
export const CAN_EDIT_COMPANY_NAME_ROLES = ['super_admin', 'org_owner'] as const

/**
 * Whether a role may update the Organization Name (DCP-158).
 * Only Super-admin and Account Owner (org_owner).
 */
export function canUpdateCompanyName(role: UserRole): boolean {
  return CAN_EDIT_COMPANY_NAME_ROLES.includes(
    role as (typeof CAN_EDIT_COMPANY_NAME_ROLES)[number],
  )
}

/**
 * Whether a user may VIEW the Organization Settings page. Broader than edit:
 * super_admin / org_owner (can edit), plus system 'admin' and any product role
 * holding 'manage_settings' (they see the name field disabled). DCP-158 admits
 * org_owner, who was previously blocked from the page entirely.
 */
export function canViewOrganizationSettings(profile: {
  role: UserRole
  productRole?: ProductRole | null
}): boolean {
  return (
    canUpdateCompanyName(profile.role) ||
    profile.role === 'admin' ||
    hasPermission(profile.productRole, 'manage_settings')
  )
}

// =============================================================================
// Product role + permissions
// =============================================================================
// Mirror of `has_permission()` SQL function in
// `supabase/migrations/20260508144442_invite_product_role.sql`. Keep in sync.
//
// product_role is orthogonal to the system user_role. System role drives
// platform-level access (super_admin, org_owner, etc.); product_role drives
// feature-level permissions inside a tenant. Drift between this matrix and
// the SQL helper is a security bug — any change must land in both places in
// the same PR.
// =============================================================================

/**
 * Per-organization feature-level role enum. Matches `public.product_role` in DB.
 */
export const productRoleSchema = z.enum([
  'accounts_payable',
  'treasury',
  'view_only',
  'admin',
])

/**
 * Permission keys recognized by `has_permission()` and the matrix below.
 * Adding a new key requires updating both the SQL function and
 * PRODUCT_ROLE_PERMISSIONS in the same migration/PR.
 */
export const permissionSchema = z.enum([
  'view_banking',
  'manage_banking',
  // DCP-167: edit the LOC borrowing base. Deliberately SEPARATE from
  // manage_banking so Treasury can edit LOC without gaining edit rights over
  // the rest of banking (a temporary BA constraint — Treasury must NOT manage
  // general banking services yet). manage_banking stays accounts_payable/admin.
  'manage_loc',
  'view_projections',
  'manage_projections',
  'view_cash_position',
  'manage_users',
  'manage_settings',
])

export type ProductRole = z.infer<typeof productRoleSchema>
export type Permission = z.infer<typeof permissionSchema>

/**
 * Authoritative permission matrix. Frozen at module load via `as const` so a
 * stray push to one of the readonly arrays would be a compile-time error.
 *
 * Read as: PRODUCT_ROLE_PERMISSIONS[role] = exhaustive list of permissions
 * granted to that role. Roles missing from the map don't exist; permissions
 * missing from a role's list are denied.
 */
export const PRODUCT_ROLE_PERMISSIONS: Record<ProductRole, readonly Permission[]> = {
  // Accounts Payable manages all of banking (manage_banking) and therefore LOC
  // editing too (manage_loc).
  accounts_payable: ['view_banking', 'manage_banking', 'manage_loc', 'view_cash_position'],
  // Treasury owns the LOC borrowing-base workflow (DCP-167), so it holds
  // manage_loc — but NOT manage_banking: per a temporary BA constraint Treasury
  // must not be able to edit general banking services yet, only LOC. Keep this
  // matrix in lockstep with the SQL has_permission() function.
  treasury: ['view_banking', 'manage_loc', 'view_projections', 'manage_projections', 'view_cash_position'],
  view_only: ['view_banking', 'view_projections', 'view_cash_position'],
  admin: [
    'view_banking',
    'manage_banking',
    'manage_loc',
    'view_projections',
    'manage_projections',
    'view_cash_position',
    'manage_users',
    'manage_settings',
  ],
} as const

/**
 * Human-readable display labels for each product role.
 * Single source of truth for UI — keep in sync with `productRoleSchema`.
 * Typed as `Record<ProductRole, string>` so adding a role to the enum forces
 * a label here at compile time (exhaustiveness).
 */
export const PRODUCT_ROLE_LABELS: Record<ProductRole, string> = {
  accounts_payable: 'Accounts Payable',
  treasury: 'Treasury',
  view_only: 'View Only',
  admin: 'Admin',
} as const

/**
 * Check whether a product role grants a permission.
 *
 * Returns false for null/undefined product roles (platform staff, legacy
 * profiles before the product_role backfill, or rows where the column is intentionally
 * cleared) so call sites can rely on a strict boolean without short-circuiting
 * the null case themselves. Mirrors the NULL handling in the SQL helper.
 */
export function hasPermission(
  productRole: ProductRole | null | undefined,
  permission: Permission,
): boolean {
  if (productRole === null || productRole === undefined) return false
  return PRODUCT_ROLE_PERMISSIONS[productRole].includes(permission)
}

/**
 * Check if a profile can invite users to an organization.
 *
 * Two independent paths: legacy system roles in CAN_INVITE_ROLES (super_admin,
 * admin, org_owner) keep the privilege regardless of product_role; the new
 * product Admin role grants it on top of that. Both are checked because a
 * tenant Admin (system role 'user') must be able to invite, and a super_admin
 * with no product_role assigned still must be able to invite.
 */
export function canInviteUsers(profile: {
  role: UserRole
  product_role?: ProductRole | null
}): boolean {
  if (CAN_INVITE_ROLES.includes(profile.role as (typeof CAN_INVITE_ROLES)[number])) {
    return true
  }
  return profile.product_role === 'admin'
}

/**
 * TOTP verification schema for MFA challenge step.
 * Validates that the code is exactly 6 digits.
 */
export const totpVerifySchema = z.object({
  code: z
    .string()
    .min(1, 'Verification code is required')
    .length(6, 'Verification code must be 6 digits')
    .regex(/^\d{6}$/, 'Verification code must contain only digits'),
})

export type UserRole = z.infer<typeof userRoleSchema>
export type InviteType = z.infer<typeof inviteTypeSchema>
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>
export type AcceptInvitationInput = z.infer<typeof acceptInvitationSchema>
export type TotpVerifyInput = z.infer<typeof totpVerifySchema>

/**
 * Raw form field type for loginSchema — email is plain string (pre-transform).
 * Use as the TFieldValues generic for useForm<LoginFormValues> so that
 * defaultValues and register() accept plain strings.
 */
export type LoginFormValues = z.input<typeof loginSchema>

/**
 * Raw form field type for registerSchema — email is plain string (pre-transform).
 * Use as the TFieldValues generic for useForm<RegisterFormValues>.
 */
export type RegisterFormValues = z.input<typeof registerSchema>

/**
 * Raw form field type for forgotPasswordSchema — email is plain string (pre-transform).
 * Use as the TFieldValues generic for useForm<ForgotPasswordFormValues>.
 */
export type ForgotPasswordFormValues = z.input<typeof forgotPasswordSchema>